import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import type { z } from 'zod';
import type { CrearAsambleaRequest } from '@condo/contracts';
import { CrearAsambleaRequestSchema } from '@condo/contracts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCrearAsamblea } from './api';

interface Props {
  condoId: string;
  onCreated?: (asambleaId: string) => void;
}

// Zod 4 + @hookform/resolvers v5: el form opera sobre el tipo de "input".
type CrearAsambleaInput = z.input<typeof CrearAsambleaRequestSchema>;

function nowPlusHours(h: number): string {
  const d = new Date(Date.now() + h * 60 * 60 * 1000);
  return d.toISOString().slice(0, 16);
}

export function AsambleaForm({ condoId, onCreated }: Props) {
  const mutation = useCrearAsamblea(condoId);

  const form = useForm<CrearAsambleaInput, unknown, CrearAsambleaRequest>({
    resolver: zodResolver(CrearAsambleaRequestSchema),
    defaultValues: {
      tipo: 'ordinaria',
      // Default: 15 days from now at 10:00 (minimum ordinaria notice)
      fechaProgramada: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 16),
      lugar: '',
      plazoVotacionDesde: nowPlusHours(14 * 24),
      plazoVotacionHasta: nowPlusHours(15 * 24),
      presidenteId: '',
    },
  });

  async function onSubmit(values: CrearAsambleaRequest) {
    try {
      const asamblea = await mutation.mutateAsync(values);
      toast.success('Asamblea creada en borrador');
      form.reset();
      onCreated?.(asamblea.id);
    } catch {
      toast.error('No se pudo crear la asamblea');
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      {/* Tipo */}
      <div>
        <Label>Tipo de asamblea</Label>
        <Select
          value={form.watch('tipo')}
          onValueChange={(v) =>
            form.setValue('tipo', v as CrearAsambleaRequest['tipo'], {
              shouldValidate: true,
            })
          }
        >
          <SelectTrigger className="mt-1.5">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ordinaria">Ordinaria</SelectItem>
            <SelectItem value="extraordinaria">Extraordinaria</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Fecha programada */}
      <div>
        <Label htmlFor="fechaProgramada">Fecha y hora programada</Label>
        <Input
          id="fechaProgramada"
          type="datetime-local"
          className="mt-1.5"
          {...form.register('fechaProgramada')}
        />
        {form.formState.errors.fechaProgramada && (
          <p className="mt-1 text-xs text-[var(--color-danger)]">
            {form.formState.errors.fechaProgramada.message}
          </p>
        )}
      </div>

      {/* Lugar */}
      <div>
        <Label htmlFor="lugar">Lugar de realizacion</Label>
        <Input
          id="lugar"
          placeholder="Ej. Salon de eventos — piso 2"
          className="mt-1.5"
          {...form.register('lugar')}
        />
        {form.formState.errors.lugar && (
          <p className="mt-1 text-xs text-[var(--color-danger)]">
            {form.formState.errors.lugar.message}
          </p>
        )}
      </div>

      {/* Plazo de votacion */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="plazoDesde">Votacion desde</Label>
          <Input
            id="plazoDesde"
            type="datetime-local"
            className="mt-1.5"
            {...form.register('plazoVotacionDesde')}
          />
        </div>
        <div>
          <Label htmlFor="plazoHasta">Votacion hasta</Label>
          <Input
            id="plazoHasta"
            type="datetime-local"
            className="mt-1.5"
            {...form.register('plazoVotacionHasta')}
          />
        </div>
      </div>

      {/* Presidente ID */}
      <div>
        <Label htmlFor="presidenteId">ID del presidente de mesa (ULID del propietario)</Label>
        <Input
          id="presidenteId"
          placeholder="01XXXXXXXXXXXXXXXXXXXXXXXXX"
          className="mt-1.5 font-mono text-sm"
          {...form.register('presidenteId')}
        />
        {form.formState.errors.presidenteId && (
          <p className="mt-1 text-xs text-[var(--color-danger)]">
            {form.formState.errors.presidenteId.message}
          </p>
        )}
        <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
          Podras agregar las materias del orden del dia despues de crear el borrador.
        </p>
      </div>

      <div className="flex justify-end border-t border-[var(--color-hairline)] pt-4">
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Creando...
            </>
          ) : (
            'Crear borrador'
          )}
        </Button>
      </div>
    </form>
  );
}
